import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LucideAngularModule, Eye, Search } from 'lucide-angular';

import { DocumentService } from '../../../../core/services/document.service';
import { ModalDataService } from '../../../../core/services/modal-data.service';
import { ModalService } from '../../../../core/services/modal.service';
import { ToastService } from '../../../../core/services/toast.service';

type EventDocument = {
  _id?: string;
  id?: string;
  filename?: string;
  url?: string;
  isFavorite?: boolean;
  documentType?: {
    _id?: string;
    id?: string;
    label?: string;
    value?: string;
  };
  createdAt?: string | Date;
};

@Component({
  selector: 'app-select-existing-documents-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './select-existing-documents-modal.component.html',
})
export class SelectExistingDocumentsModalComponent implements OnInit {
  readonly SearchIcon = Search;
  readonly EyeIcon = Eye;

  documents: EventDocument[] = [];
  selectedIds = new Set<string>();
  searchTerm = '';
  activePreviewDocument: EventDocument | null = null;
  previewUrl: string | null = null;
  safePreviewUrl: SafeResourceUrl | null = null;
  previewLoading = false;

  constructor(
    private modalService: ModalService,
    private documentService: DocumentService,
    private toastService: ToastService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    const modalData = ModalDataService.getData();
    this.documents = modalData?.documents || [];

    const preselectedIds: string[] = modalData?.selectedIds || [];
    preselectedIds.forEach((id) => this.selectedIds.add(id));
  }

  get filteredDocuments(): EventDocument[] {
    if (!this.searchTerm.trim()) {
      return this.documents;
    }

    const search = this.searchTerm.toLowerCase().trim();
    return this.documents.filter((doc) => {
      const filename = doc.filename?.toLowerCase() || '';
      const typeLabel = doc.documentType?.label?.toLowerCase() || '';
      return filename.includes(search) || typeLabel.includes(search);
    });
  }

  toggleDocument(documentId: string): void {
    if (this.selectedIds.has(documentId)) {
      this.selectedIds.delete(documentId);
    } else {
      this.selectedIds.add(documentId);
    }
  }

  isSelected(documentId: string): boolean {
    return this.selectedIds.has(documentId);
  }

  setActivePreviewDocument(doc: EventDocument): void {
    this.activePreviewDocument = doc;
    this.previewUrl = null;
    this.safePreviewUrl = null;

    if (!doc.url) {
      return;
    }

    this.previewLoading = true;
    this.documentService.getPresignedUrl(doc.url).subscribe({
      next: (response) => {
        this.previewUrl = response.url;
        this.safePreviewUrl =
          this.isPreviewablePdf(doc.filename)
            ? this.sanitizer.bypassSecurityTrustResourceUrl(response.url)
            : null;
        this.previewLoading = false;
      },
      error: () => {
        this.previewLoading = false;
        this.toastService.error('Erreur lors du chargement de la previsualisation');
      },
    });
  }

  isPreviewableImage(filename?: string): boolean {
    const name = (filename || '').toLowerCase();
    return (
      name.endsWith('.png') ||
      name.endsWith('.jpg') ||
      name.endsWith('.jpeg') ||
      name.endsWith('.webp') ||
      name.endsWith('.gif')
    );
  }

  isPreviewablePdf(filename?: string): boolean {
    return (filename || '').toLowerCase().endsWith('.pdf');
  }

  onSearchChange(): void {
    if (this.filteredDocuments.length === 0) {
      this.activePreviewDocument = null;
      this.previewUrl = null;
      this.safePreviewUrl = null;
    }
  }

  confirmSelection(): void {
    this.modalService.emitEvent({
      type: 'existingDocumentsSelected',
      data: {
        documentIds: Array.from(this.selectedIds),
      },
    });
    this.modalService.closeAll();
  }

  close(): void {
    this.modalService.closeAll();
  }

  getDocumentId(doc: EventDocument): string {
    return doc._id || doc.id || '';
  }
}
